/**
 * `/nagrania` — every coached client's recordings (R27), as variant A of
 * `docs/design/nagrania-klientow-2026-09-24/` draws them: the header, the client list, and the wall.
 *
 * A client picked in the list narrows the wall through `?klient=`, so the narrowing is an address
 * Back undoes. A tile opens the client's card on Forma with the full-screen player over it — the
 * one player every recording opens in.
 *
 * The mock's „Wszystkie / Nieobejrzane" switch is not drawn: the read has no unread filter, and
 * narrowing only the pages already loaded would hide unwatched recordings further down the
 * collection while claiming to show them all.
 */

import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { screenMotion, useScreenMotionPreference } from '~/shared/design-system/motion';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { AvatarChip } from '~/shared/ui/AvatarChip';
import shell from '~/shared/ui/canonScreen.module.css';
import ink from '~/shared/ui/inkButton.module.css';
import header from '~/shared/ui/screenHeader.module.css';
import skel from '~/shared/ui/skeleton.module.css';

import { useRecordings } from '../lib/useRecordings';
import { validateRecordingsSearch } from '../model/recordings';

import { RecordingsClientList } from './RecordingsClientList';
import { OutGlyph } from './RecordingsIcons';
import { RecordingsWall } from './RecordingsWall';
import styles from './RecordingsScreen.module.css';

export function RecordingsScreen() {
  const { t } = useTranslation();
  const reducedMotion = useScreenMotionPreference();
  const navigate = useNavigate();
  const customerId = validateRecordingsSearch(useSearch({ strict: false })).klient ?? null;
  const state = useRecordings(customerId);
  const listGate = useSkeletonGate(state.listPending);
  // A client this trainer does not coach — an old link, or coaching that ended. The address is
  // corrected in place (REPLACE, so Back does not return to it) and every client's wall is read.
  useEffect(() => {
    if (state.clientGone) void navigate({ to: '/nagrania', search: {}, replace: true });
  }, [state.clientGone, navigate]);
  const wallGate = useSkeletonGate(state.wallPending);
  const { selected } = state.view;
  // A narrowed address holds the avatar's room from the first frame, so the name does not slide
  // right when the list lands and says who it is.
  const narrowed = customerId !== null && (selected !== null || listGate.waiting);

  return (
    <main className={shell.page}>
      <motion.div className={shell.measure} {...screenMotion('rise', { reducedMotion })}>
        <header className={styles.head}>
          {narrowed ? (
            <span className={styles.headAvatar}>
              {selected === null ? null : (
                <AvatarChip avatarUrl={selected.person.avatarUrl} initials={selected.person.initials} variant="clip" />
              )}
            </span>
          ) : null}
          <div className={styles.headText}>
            <h1 className={`${String(header.title)} ${String(styles.title)}`} tabIndex={-1}>
              {selected?.person.fullName ?? t('web:recordings.title')}
            </h1>
            <p className={styles.sub}>
              {listGate.waiting || state.listFailed ? (
                <i className={listGate.skeleton ? `${String(skel.bar)} ${String(styles.skelSub)}` : styles.skelSub} />
              ) : (
                <span className={listGate.reveal}>{selected?.countWords ?? state.view.summary}</span>
              )}
            </p>
          </div>
          {selected === null ? null : (
            <Link
              className={`${String(ink.ink)} ${String(styles.cardLink)}`}
              params={{ clientId: String(selected.customerId) }}
              search={{ tab: 'forma' }}
              to="/klienci/$clientId"
            >
              {t('web:recordings.openCard')}
              <OutGlyph className={styles.cardGlyph} />
            </Link>
          )}
        </header>

        <div className={styles.body}>
          <RecordingsClientList
            customerId={customerId}
            failed={state.listFailed}
            gate={listGate}
            onPick={(next) => {
              // PUSHES: a narrowing is the trainer's own step, and Back is the way to widen it again.
              void navigate({ to: '/nagrania', search: next === null ? {} : { klient: next } });
            }}
            view={state.view}
          />
          <RecordingsWall
            gate={wallGate}
            onOpenClip={(clipId) => {
              const clientId = state.view.clientOfClip.get(clipId);
              if (clientId === undefined) return;
              void navigate({
                to: '/klienci/$clientId',
                params: { clientId: String(clientId) },
                search: { tab: 'forma', clip: clipId },
              });
            }}
            state={state}
          />
        </div>
      </motion.div>
    </main>
  );
}
